/**
 * Generic role-group management page (admins, volunteers, catch-all /role-group/:groupCode).
 * Tabbed list with {@link SlidePanel}; render props default to Admin views.
 */

import * as React from 'react';
import { LayoutGrid } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/forms/Button';
import { SlidePanel } from '@/components/common/SlidePanel';
import { AdminsDataTable } from '@/components/admins/AdminsDataTable';
import { CreateAdminView } from '@/components/admins/CreateAdminView';
import { EditAdminView } from '@/components/admins/EditAdminView';
import { useDetailsView } from '@/hooks/useDetailsView';
import { useViewMode } from '@/hooks/useViewMode';
import { ManagementPage } from '@/components/common/ManagementPage';
import { usePageActions } from '@/hooks/usePageActions';
import { DynamicIcon } from '@/components/forms/IconPicker';
import { useRoleGroupTabs } from '@/hooks/useRoleGroupTabs';
import type { ViewMode } from '@/types/data-table.types';

/** Render-prop payload for the role-group {@link DataTable} (defaults to {@link AdminsDataTable}). */
export interface RoleGroupDataTableRenderProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  expandedRowId: string | null;
  onToggleRow: (id: string) => void;
  onCloseDetails: () => void;
  onEdit: (id: string) => void;
  enableFilters: boolean;
  enabledRoles: string[];
  pageActions: { canEditRow?: boolean; canDeleteRow?: boolean; canExportCsv?: boolean };
}

/** Render-prop payload for the create {@link SlidePanel} (defaults to {@link CreateAdminView}). */
export interface RoleGroupCreateViewRenderProps {
  onClose: () => void;
  onSuccess: () => void;
  initialRole?: string;
}

/** Render-prop payload for the edit {@link SlidePanel} (defaults to {@link EditAdminView}). */
export interface RoleGroupEditViewRenderProps {
  editId: string;
  onClose: () => void;
  onSuccess: () => void;
}

/** Props for {@link DynamicRoleGroupManagementPage}. */
export interface DynamicRoleGroupManagementPageProps {
  /**
   * The interface_name to read IE from (equals role_group code with underscores
   * replaced by hyphens, e.g. 'admins', 'steering', 'service-users').
   */
  interfaceName: string;
  /**
   * element_id of the parent menu IE inside the management interface
   * (e.g. 'menu-admins', 'menu-steering').
   */
  menuId: string;
  title: string;
  subtitle?: string;
  createLabel?: string;
  /**
   * Query-key segment to invalidate on success (e.g. 'admin', 'steering').
   */
  userQueryKey?: string;
  tabsAriaLabel?: string;
  /**
   * Color for the all-roles tab (icon + active state).
   * Set in the module config (e.g. {@link ADMIN_TAB_COLORS}.all).
   */
  allTabColor?: string;
  /** Custom {@link DataTable}. Defaults to {@link AdminsDataTable}. */
  renderDataTable?: (props: RoleGroupDataTableRenderProps) => React.ReactNode;
  /** Custom CreateView. Defaults to {@link CreateAdminView}. */
  renderCreateView?: (props: RoleGroupCreateViewRenderProps) => React.ReactNode;
  /** Custom EditView. Defaults to {@link EditAdminView}. */
  renderEditView?: (props: RoleGroupEditViewRenderProps) => React.ReactNode;
}

/** Generic management page for a dynamic role group (tabs, table, forms). */
export const DynamicRoleGroupManagementPage: React.FC<DynamicRoleGroupManagementPageProps> = ({
  interfaceName,
  menuId,
  title,
  subtitle = 'Créez, modifiez et gérez tous vos collaborateurs',
  createLabel = 'Créer un collaborateur',
  userQueryKey = 'admin',
  tabsAriaLabel = 'Filtrage par rôle',
  allTabColor = 'var(--color-5-nuance-1)',
  renderDataTable,
  renderCreateView,
  renderEditView,
}) => {
  const { canCreate, canEditRow, canDeleteRow, canExportCsv } = usePageActions(interfaceName);
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [createFormOpen, setCreateFormOpen] = React.useState(false);
  const { openRowId, openRow, closeRow } = useDetailsView();
  const [editId, setEditId] = React.useState<string | null>(null);
  const [initialRole, setInitialRole] = React.useState<string | undefined>(undefined);
  const { viewMode, setViewMode } = useViewMode(`${interfaceName}-management`);

  const { visibleTabs, showTabBar, activeRoleFilter, setActiveRoleFilter, enabledRoles } =
    useRoleGroupTabs(interfaceName, menuId);

  /** Deep-link: open create / edit / view from URL params, then strip them. */
  React.useEffect(() => {
    const editParamId = searchParams.get('edit');
    const viewId = searchParams.get('view');
    const shouldCreate = searchParams.get('create') === 'true';
    const tab = searchParams.get('tab');
    const role = searchParams.get('role');

    if (editParamId) {
      setEditId(editParamId);
      setCreateFormOpen(false);
      closeRow();
      searchParams.delete('edit');
      setSearchParams(searchParams, { replace: true });
    }

    if (viewId) {
      openRow(viewId);
      setCreateFormOpen(false);
      setEditId(null);
      searchParams.delete('view');
      setSearchParams(searchParams, { replace: true });
    }

    if (shouldCreate) {
      setCreateFormOpen(true);
      setEditId(null);
      closeRow();
      if (role) setInitialRole(role);
      searchParams.delete('create');
      if (!role) searchParams.delete('role');
      setSearchParams(searchParams, { replace: true });
    }

    if (tab) {
      setActiveRoleFilter(tab);
    }
  }, [searchParams, setSearchParams, closeRow, openRow, setActiveRoleFilter]);

  /** Close both {@link SlidePanel}s and drop the create-form initial role. */
  const handleCloseForm = React.useCallback(() => {
    setCreateFormOpen(false);
    setEditId(null);
    setInitialRole(undefined);
  }, []);

  /** Close the form and refresh the role-group user list. */
  const handleSuccess = React.useCallback(() => {
    setCreateFormOpen(false);
    setEditId(null);
    setInitialRole(undefined);
    void queryClient.invalidateQueries({ queryKey: ['users', userQueryKey] });
  }, [queryClient, userQueryKey]);

  const pageActions = { canEditRow, canDeleteRow, canExportCsv };

  /** Custom {@link DataTable} render prop, else the admin table with the same wiring. */
  const dataTableNode = renderDataTable ? (
    renderDataTable({
      viewMode,
      onViewModeChange: setViewMode,
      expandedRowId: openRowId,
      onToggleRow: openRow,
      onCloseDetails: closeRow,
      onEdit: (id) => {
        setEditId(id);
        setCreateFormOpen(false);
        closeRow();
      },
      enableFilters: true,
      enabledRoles: enabledRoles ?? [],
      pageActions,
    })
  ) : (
    <AdminsDataTable
      expandedRowId={openRowId}
      onToggleRow={openRow}
      onCloseDetails={closeRow}
      onEditAdmin={(id) => {
        setEditId(id);
        setCreateFormOpen(false);
        closeRow();
      }}
      enableFilters={true}
      enabledRoles={enabledRoles}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      pageActions={pageActions}
    />
  );

  /** Custom create view, else {@link CreateAdminView}. */
  const createViewNode = renderCreateView ? (
    renderCreateView({ onClose: handleCloseForm, onSuccess: handleSuccess, initialRole })
  ) : (
    <CreateAdminView
      onClose={handleCloseForm}
      onSuccess={handleSuccess}
      initialRole={initialRole}
    />
  );

  /** Custom edit view, else {@link EditAdminView} (only when an edit id is set). */
  const editViewNode = React.useMemo(() => {
    if (!editId) return null;
    if (renderEditView) {
      return renderEditView({ editId, onClose: handleCloseForm, onSuccess: handleSuccess });
    }
    return (
      <EditAdminView
        key={editId}
        adminId={editId}
        onClose={handleCloseForm}
        onSuccess={handleSuccess}
      />
    );
  }, [editId, renderEditView, handleCloseForm, handleSuccess]);

  return (
    <ManagementPage
      title={title}
      subtitle={subtitle}
      actions={
        canCreate ? (
          <Button
            preset="create-entity"
            onClick={() => {
              setInitialRole(activeRoleFilter !== 'all' ? activeRoleFilter : undefined);
              setCreateFormOpen(true);
              setEditId(null);
              closeRow();
            }}
          >
            {createLabel}
          </Button>
        ) : undefined
      }
      tabs={{
        items: [
          {
            id: 'all',
            label: 'Tous',
            icon: <LayoutGrid className="h-4 w-4" />,
            color: allTabColor,
          },
          ...visibleTabs.map((tab) => ({
            id: tab.id,
            label: tab.label,
            icon: <DynamicIcon name={tab.icon} className="h-4 w-4" />,
            color: tab.color ?? undefined,
          })),
        ],
        activeTab: activeRoleFilter,
        onTabChange: (tab: string) => {
          setActiveRoleFilter(tab);
          closeRow();
        },
        variant: 'default',
        size: 'md',
        ariaLabel: tabsAriaLabel,
        visible: showTabBar,
      }}
    >
      {dataTableNode}

      <SlidePanel isOpen={createFormOpen} onClose={handleCloseForm}>
        {createViewNode}
      </SlidePanel>

      <SlidePanel isOpen={!!editId} onClose={handleCloseForm}>
        {editViewNode}
      </SlidePanel>
    </ManagementPage>
  );
};
